import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import Box from '@material-ui/core/Box';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { injectIntl } from 'react-intl';
import Hidden from '@material-ui/core/Hidden';

import { ADGMAlert, ADGMImage } from 'aurora-components';

import Divider from '@material-ui/core/Divider';
import { getPropertType, keyEmptyCheck, getPropertTypeLabel } from 'aurora-utils/utils';

import messages from 'aurora-translations';
import { addPMAProperties, removePMAProperties } from 'aurora-composition/modules/pma/actions';
import { useDispatch, useSelector } from 'react-redux';
import history from 'aurora-utils/history';
import RemovePropertyModal from 'aurora-containers/Routes/Service/PMA/PMAForm/AddProperty/removePropertyModal';
import PropertyBadge from './Badge';
import PropertyCollapse from './Collapse';
import styles from './styles';

function PropertySummary(props) {
  const {
    classes,
    intl,
    locale,
    switchType,
    property,
    isLease,
    isMultiLease,
    isOwnership,
    isMortgage,
    expandedId,
    setExpandedId,
    selectable,
    multi,
    selected,
    setSelected,
    getPropertyTitle,
    fetchBuildingProperties,
    isPMA,
    viewPMAUnits,
    viewLeaseUnitsHandler,
    isPMABank,
    isPmc,
  } = props;
  const params = new URLSearchParams(window.location.search);
  const applicationId = params.get('id');
  const isRenewal = params.get('isRenewal') === 'true';
  const isAmendment = params.get('isAmendment') === 'true';
  const isRegistration = params.get('isRegistration') === 'true';
  const dispatch = useDispatch();
  const unitValidation = useSelector(state => state.pmaReducer.unitValidation);
  const [buildingProperties, setBuildingProperties] = useState(null);
  const [isOpenRemovePMAPropertyModal, setIsOpenRemovePMAPropertyModal] = useState(false);
  const [pmaLoading, setPmaLoading] = useState(false);

  const propertyEnum = useMemo(() => getPropertTypeLabel(property?.propertyType, property, locale === 'ar'), [
    property,
  ]);

  const isBuilding = useMemo(() => property?.propertyType === 2, [property]);

  const isUnit = useMemo(() => property?.propertyType === 4, [property]);
  const isLand = useMemo(() => property?.propertyType === 1, [property]);

  const isAllUnitsSelected = useMemo(() => {
    const buldingUnits = multi && selected?.filter(item => item.buildingRegNumber === property.buildingRegNumber);
    if (isEmpty(buldingUnits)) return false;
    return buldingUnits.length === buildingProperties?.properties?.length;
  }, [multi, selected, property.buildingRegNumber, buildingProperties]);
  const propertyDetails = useMemo(() => {
    if (isPMABank && isUnit) {
      return {
        prp_number: property?.buildingRegNumber,
        unt_number: property?.unitRegNum,
        unit_number: property?.unitNumber,
        bedrooms: property?.bedrooms,
        area: property?.unitArea ? `${property?.unitArea} sqm` : null,
      };
    }
    return {
      district: property?.districtName,
      community: property?.communityName,
      ...(isUnit &&
        property?.plotNumber && {
          plot_number: property?.plotNumber,
        }),
      ...((((isOwnership || isMortgage) && isUnit) || (isLease && (isBuilding || isUnit))) && {
        building_name: property?.buildingName,
      }),
      // ...(isUnit &&
      //   property?.unitNumber && {
      //     unit_number: property?.unitNumber,
      //   }),
      ...(isLease &&
        isBuilding &&
        property?.buildingRegNumber && {
          prp_number: property?.buildingRegNumber,
        }),
      ...(isLease &&
        isUnit &&
        property?.unitRegNum && {
          unt_number: property?.unitRegNum,
        }),
    };
  }, [property]);

  const propertyTitle = useMemo(() => {
    if (isLand)
      return (
        <>
          {keyEmptyCheck('plot_number')}: {property?.plotNumber}
        </>
      );
    if (isBuilding) return property?.buildingName;
    return [
      ...(property?.unitNumber ? [`${intl.formatMessage({ ...messages.Unit })} ${property?.unitNumber}`] : []),
      ...(property?.buildingNumber
        ? [` ${intl.formatMessage({ ...messages.BUILDING })} ${property?.buildingNumber}`]
        : []),
    ].join(', ');
  }, [property]);
  const propertyInfoList = useMemo(() => {
    const data = [];

    if (isLand && (property?.landUseEn || property?.landUseAr))
      data.push(locale === 'ar' ? property?.landUseAr : property?.landUseEn);

    if (isUnit && (property?.unitUsageName || property?.landUseEn))
      data.push(property?.unitUsageName || property?.landUseName);

    if (isBuilding && property?.buildingUsageName) data.push(property?.buildingUsageName);

    if (isMortgage && property?.mortgageType) data.push(property?.mortgageType);

    // if (isOwnership && property?.ownershipPercentage && property?.ownerShipTypeName)
    //   data.push(`${property?.ownershipPercentage}% ${property?.ownerShipTypeName}`);

    if (isBuilding && property?.unitsCount)
      data.push(`${property?.unitsCount} ${intl.formatMessage({ ...messages.units })}`);

    return data;
  }, [property, isLand, isUnit, isOwnership, isBuilding, isMortgage]);

  const isChecked = useMemo(() => {
    // For bulding, check for all units
    if (isBuilding) return isAllUnitsSelected;
    return multi && selected?.some(item => item.propertyID === property.propertyID);
  }, [property.propertyType, isAllUnitsSelected, multi, selected]);
  const isPMAPropertySelected = Array.isArray(selected)
    ? (selected || []).findIndex(prop => prop.propertyID === property.propertyID) > -1
    : false;
  const isIndeterminate = useMemo(() => {
    return (
      multi &&
      isBuilding &&
      !isChecked &&
      property.buildingRegNumber &&
      selected?.some(item => item.buildingRegNumber === property.buildingRegNumber)
    );
  }, [multi, isChecked, property.propertyType, selected]);

  const handleExpandToggle = useCallback(
    async selectAll => {
      if (!isPMA && !isMultiLease) {
        // If the function has been called by item click
        if (selectAll === true && buildingProperties) {
          if (isAllUnitsSelected) {
            const newSelected = selected.filter(item => item.buildingRegNumber !== property.buildingRegNumber);
            setSelected(newSelected);
          } else {
            const newSelected = [...(selected || []), ...(buildingProperties?.properties || [])];
            setSelected(newSelected);
          }
          setExpandedId(property.propertyID);
          return;
        }

        // Normal flow with building badge clicked
        if (expandedId === property.propertyID) {
          setExpandedId(null);
          return;
        }
        if (buildingProperties) {
          setExpandedId(property.propertyID);
          return;
        }
        const res = await fetchBuildingProperties(property.propertyID);
        if (!res?.success) return;
        if (selectAll === true) setSelected([...(selected || []), ...(res?.result?.properties || [])]);
        setBuildingProperties(res?.result || null);
        setExpandedId(property.propertyID);
      }
    },
    [buildingProperties, expandedId, property.propertyID, selected],
  );

  const onItemClick = useCallback(
    async data => {
      if (multi) {
        // If the selected is building
        if (isBuilding) {
          if (isAllUnitsSelected || isIndeterminate) {
            const newSelected = selected.filter(item => item.buildingRegNumber !== property.buildingRegNumber);
            setSelected(newSelected);
            if (expandedId === property.propertyID) setExpandedId(null);
            return;
          }
          await handleExpandToggle(true);
          return;
        }

        // For all other cases
        const newSelected = [...(selected || [])];
        const alreadyExist = newSelected.some(item => item.propertyID === data.propertyID);
        if (alreadyExist) {
          setSelected(newSelected.filter(item => item.propertyID !== data.propertyID));
        } else {
          setSelected([...newSelected, data]);
        }
        return;
      }

      if (selected?.propertyID === data.propertyID) {
        setSelected(null);
        return;
      }
      setSelected(data);
    },
    [multi, property, isAllUnitsSelected, isIndeterminate, selected, expandedId, buildingProperties],
  );
  const currentUnitValidations =
    isUnit && unitValidation.length > 0
      ? unitValidation.find(validation => validation.data.properties[0].propertyID === property.propertyID)
      : {};

  const PmaUnitSelectionHandler = async e => {
    e.stopPropagation();
    if (isPMAPropertySelected) {
      setIsOpenRemovePMAPropertyModal(true);
    } else {
      setPmaLoading(true);
      const result = await dispatch(
        addPMAProperties({
          applicationId,
          properties: [property],
          isOverride: false,
          isUnit: true,
          isRegistration,
          isRenewal,
          isAmendment,
        }),
      );
      setPmaLoading(false);
      result && setSelected(selected ? [...selected, property] : [property]);
    }
  };

  const isSelected = useMemo(
    () =>
      multi
        ? (selected || [])?.find(selectedProperty => selectedProperty?.propertyID === property.propertyID) !== undefined
        : selected?.propertyID === property.propertyID,
    [property, selected, multi],
  );
  return (
    <>
      <div
        className={classNames(classes.listItemContainer, {
          [classes.listItemContainerChecked]: isSelected,
        })}
      >
        <ListItem
          className={classNames({ [classes.disableCard]: pmaLoading })}
          disableRipple={!selectable}
          dense
          button={selectable && !isPMA && !isMultiLease}
          {...(selectable
            ? {
                onClick:
                  (isPMABank || isPmc) && isChecked && isUnit
                    ? () => onItemClick(property)
                    : isPMA && isUnit
                    ? PmaUnitSelectionHandler
                    : () => onItemClick(property),
              }
            : {})}
        >
          <ListItemText
            id={property.propertyID}
            primary={
              <div
                className={classNames(classes.containerItem, {
                  [classes.listItemTextBuilding]: isBuilding,
                })}
              >
                <Grid container alignItems='center'>
                  <Grid className={classes.sectionMargin} item xs={8} sm={10} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {!isBuilding &&
                        selectable &&
                        (multi ? (
                          <Checkbox
                            edge='start'
                            checked={isChecked || false}
                            indeterminate={isIndeterminate || false}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{
                              'aria-labelledby': property.propertyID,
                            }}
                            classes={{
                              root: classes.itemCheckbox,
                              checked: classes.itemCheckboxChecked,
                              indeterminate: classes.itemCheckboxIndeterminate,
                            }}
                          />
                        ) : (
                          <Radio
                            edge='start'
                            checked={selected?.propertyID === property.propertyID || false}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{
                              'aria-labelledby': property.propertyID,
                            }}
                            classes={{
                              root: classes.itemCheckbox,
                              checked: classes.itemCheckboxChecked,
                            }}
                          />
                        ))}

                      <Typography className={classes.title}>{propertyTitle}</Typography>
                    </Box>
                  </Grid>
                  <Hidden mdUp>
                    <Grid item container xs={4} sm={2} justifyContent='flex-end'>
                      {propertyEnum && (
                        <Box className={[classes.typeChip, propertyEnum === 'Store' && classes.studioColor]}>
                          {keyEmptyCheck(propertyEnum)}
                        </Box>
                      )}
                    </Grid>
                  </Hidden>
                  <Hidden mdUp>
                    <Grid
                      className={classNames(classes.mobileMargin, classes.propertyInfoList)}
                      item
                      container
                      justifyContent='flex-start'
                      md={12}
                    >
                      {propertyInfoList?.map(propertyInfo => (
                        <div className={classes.propertyInfoContainer}>
                          {/* {index > 0 && (
                            <span className={classes.propertyInfoIcon}>
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='4'
                                height='4'
                                viewBox='0 0 4 4'
                                fill='none'
                              >
                                <circle cx='2' cy='2' r='2' fill='#CED1D6' />
                              </svg>
                            </span>
                          )} */}
                          <Typography className={classes.propertyInfoText}>{propertyInfo}</Typography>
                        </div>
                      ))}
                    </Grid>
                  </Hidden>
                  <Grid item xs={12} sm={12} md={6} className={classes.mobileMargin}>
                    {!isBuilding && (
                      <PropertyBadge
                        locale={locale}
                        property={property}
                        isOwnership={isOwnership}
                        isLease={isLease}
                        expanded={expandedId === property.propertyID}
                        handleExpand={handleExpandToggle}
                      />
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexBasis: 'row',
                        gap: '8px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {isBuilding && (
                        <PropertyBadge
                          locale={locale}
                          property={property}
                          isOwnership={isOwnership}
                          isLease={isLease}
                          isMultiLease={isMultiLease}
                          expanded={expandedId === property.propertyID}
                          handleExpand={handleExpandToggle}
                          isPMA={isPMA}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Hidden smDown>
                    <Grid item container md={12}>
                      {propertyEnum && (
                        <Box
                          sx={{ mr: '0.5rem' }}
                          className={[classes.typeChip, propertyEnum === 'Store' && classes.studioColor]}
                        >
                          {keyEmptyCheck(propertyEnum)}
                        </Box>
                      )}
                      <div className={classes.propertyInfoList}>
                        {propertyInfoList?.map(propertyInfo => (
                          <div className={classes.propertyInfoContainer}>
                            {/* {index > 0 && (
                              <span className={classes.propertyInfoIcon}>
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='4'
                                  height='4'
                                  viewBox='0 0 4 4'
                                  fill='none'
                                >
                                  <circle cx='2' cy='2' r='2' fill='#CED1D6' />
                                </svg>
                              </span>
                            )} */}

                            <Typography className={classes.propertyInfoText}>{propertyInfo}</Typography>
                          </div>
                        ))}
                      </div>
                    </Grid>
                  </Hidden>
                </Grid>

                <Hidden smDown>
                  <Divider className={classes.divider} />
                </Hidden>

                <Hidden smDown>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box className={classes.listItemContent}>
                      {Object.keys(propertyDetails)?.map(propertyItemKey => (
                        <div>
                          <Typography className={classes.contentTitle}>{keyEmptyCheck(propertyItemKey)}</Typography>
                          <Typography className={classes.contentSubTitle}>
                            {propertyDetails[propertyItemKey]}
                          </Typography>
                        </div>
                      ))}
                    </Box>
                    {!selectable && (isUnit || getPropertType(property?.propertyType) === 'Land') && (
                      <Button
                        endIcon={
                          <ADGMImage
                            className={classNames({
                              [classes.propertyDetailButtonRotateIcon]: locale === 'ar',
                            })}
                            alt='righArrow'
                            name='arrowRight.svg'
                          />
                        }
                        className={classes.propertyDetailButton}
                        variant='text'
                        onClick={() =>
                          history.push({
                            pathname: `/app/properties/${property.propertyID}?type=${switchType}`,
                            state: { type: switchType },
                          })
                        }
                      >
                        {keyEmptyCheck('Property_details')}
                      </Button>
                    )}
                    {!selectable && isBuilding && (
                      <Button
                        endIcon={
                          <ADGMImage
                            className={classNames({
                              [classes.propertyDetailButtonRotateIcon]: locale === 'ar',
                            })}
                            alt='righArrow'
                            name='arrowRight.svg'
                          />
                        }
                        className={classes.propertyDetailButton}
                        variant='text'
                        onClick={() =>
                          history.push({
                            pathname: `/app/properties/${property.propertyID}?type=${switchType}`,
                            state: { type: switchType },
                          })
                        }
                      >
                        {keyEmptyCheck('Property_details')}
                      </Button>
                    )}
                    {(isPMA || isMultiLease) && (
                      <div className={classes.listItemActionArrow}>
                        {isBuilding && (
                          <>
                            <Button
                              endIcon={
                                <ADGMImage
                                  className={classNames({
                                    [classes.propertyDetailButtonRotateIcon]: locale === 'ar',
                                  })}
                                  alt='righArrow'
                                  name='arrowRight.svg'
                                />
                              }
                              className={classes.propertyDetailButton}
                              onClick={() =>
                                isPMA ? viewPMAUnits(property) : isMultiLease ? viewLeaseUnitsHandler(property) : null
                              }
                            >
                              {keyEmptyCheck('view_units')} ({property?.unitsCount})
                            </Button>
                          </>
                        )}
                        {/* {isUnit && (
                          <>
                            <Paper
                              className={classes.roundedButton}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isPMAPropertySelected) {
                                  setIsOpenRemovePMAPropertyModal(true);
                                } else {
                                  const result = await dispatch(
                                    addPMAProperties({
                                      applicationId,
                                      properties: [property],
                                      isOverride: false,
                                      isUnit: true,
                                      isRegistration,
                                      isRenewal,
                                      isAmendment,
                                    }),
                                  );
                                  result &&
                                    setSelected(
                                      selected
                                        ? [...selected, property]
                                        : [property],
                                    );
                                }
                              }}
                            >
                              <ADGMImage
                                src={
                                  isPMAPropertySelected
                                    ? '/images/minusIcon.svg'
                                    : '/images/plusIconRed.svg'
                                }
                                alt="menuIcon"
                                className={classes.arrowWidth}
                              />
                            </Paper>
                          </>
                        )} */}
                      </div>
                    )}
                  </Box>
                </Hidden>
                <Hidden mdUp>
                  <div>
                    <Typography className={classes.contentTitle}>{keyEmptyCheck('Address')}</Typography>
                    <Typography className={classes.contentSubTitle}>
                      {Object.values(propertyDetails)
                        ?.filter(val => !!val)
                        ?.join(', ')}
                    </Typography>
                  </div>
                </Hidden>

                <Hidden mdUp>
                  <Divider className={classes.divider} />
                </Hidden>
                <Hidden mdUp>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {!selectable && (
                      <Button
                        className={classes.propertyDetailButton}
                        variant='text'
                        endIcon={
                          <ADGMImage
                            className={classNames({
                              [classes.propertyDetailButtonRotateIcon]: locale === 'ar',
                            })}
                            alt='righArrow'
                            name='arrowRight.svg'
                          />
                        }
                        onClick={() =>
                          history.push({
                            pathname: `/app/properties/${property.propertyID}?type=${switchType}`,
                            state: { type: switchType },
                          })
                        }
                      >
                        {keyEmptyCheck('Property_details')}
                      </Button>
                    )}
                    {(isPMA || isMultiLease) && (
                      <div className={classes.listItemActionArrow}>
                        {isBuilding && (
                          <>
                            <Button
                              className={classes.propertyDetailButton}
                              endIcon={
                                <ADGMImage
                                  className={classNames({
                                    [classes.propertyDetailButtonRotateIcon]: locale === 'ar',
                                  })}
                                  alt='righArrow'
                                  name='arrowRight.svg'
                                />
                              }
                              onClick={() =>
                                isPMA ? viewPMAUnits(property) : isMultiLease ? viewLeaseUnitsHandler(property) : null
                              }
                            >
                              {keyEmptyCheck('view_units')} ({property?.unitsCount})
                            </Button>
                          </>
                        )}
                        {/* {isUnit && (
                          <>
                            <Paper
                              className={classes.roundedButton}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isPMAPropertySelected) {
                                  setIsOpenRemovePMAPropertyModal(true);
                                } else {
                                  const result = await dispatch(
                                    addPMAProperties({
                                      applicationId,
                                      properties: [property],
                                      isOverride: false,
                                      isUnit: true,
                                      isRegistration,
                                      isRenewal,
                                      isAmendment,
                                    }),
                                  );
                                  result &&
                                    setSelected(
                                      selected
                                        ? [...selected, property]
                                        : [property],
                                    );
                                }
                              }}
                            >
                              <img
                                src={
                                  isPMAPropertySelected
                                    ? '/images/minusIcon.svg'
                                    : '/images/plusIconRed.svg'
                                }
                                alt="menuIcon"
                                className={classes.arrowWidth}
                              />
                            </Paper>
                          </>
                        )} */}
                      </div>
                    )}
                  </Box>
                </Hidden>
              </div>
            }
          />
        </ListItem>
        {!isEmpty(buildingProperties?.properties) && (
          <PropertyCollapse
            expanded={expandedId === property.propertyID}
            data={buildingProperties}
            selectable={selectable}
            multi={multi}
            selected={selected}
            setSelected={setSelected}
            isLease={isLease}
            isOwnership={isOwnership}
            switchType={switchType}
            getPropertyTitle={getPropertyTitle}
            onItemClick={onItemClick}
          />
        )}
        {!isEmpty(currentUnitValidations) && (
          <div style={{ padding: '10px', width: '100%' }}>
            <ADGMAlert visible noBackground={false} type='error' className={classes.adgmAlert}>
              {keyEmptyCheck('this_unit_has_multiple_blocks_on_it')}
              <RouterLink
                onClick={() => {
                  history.push({
                    pathname: '/app/services/pma-validation',
                    state: currentUnitValidations,
                  });
                }}
              >
                {keyEmptyCheck('check_list_here')}
              </RouterLink>
            </ADGMAlert>
          </div>
        )}
        {pmaLoading && (
          <div className={classes.loadingContainer}>
            <div>
              <ADGMImage name='dotted-loader.svg' alt='dotted loader' />
              <Typography>{keyEmptyCheck('validating_unit')}...</Typography>
            </div>
          </div>
        )}
      </div>
      <RemovePropertyModal
        onClose={() => setIsOpenRemovePMAPropertyModal(false)}
        open={isOpenRemovePMAPropertyModal}
        removeProperty={async () => {
          const result = await dispatch(
            removePMAProperties({
              applicationId: +applicationId,
              plotIds: [property.propertyID],
            }),
          );
          result && setSelected(selected ? selected.filter(item => item.propertyID !== property.propertyID) : []);
        }}
        unitRegNum={property.unitRegNum}
        buildingCount={1}
      />
    </>
  );
}

PropertySummary.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  switchType: PropTypes.string.isRequired,
  property: PropTypes.object.isRequired,
  expandedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLease: PropTypes.bool.isRequired,
  isMultiLease: PropTypes.bool.isRequired,
  isOwnership: PropTypes.bool.isRequired,
  isMortgage: PropTypes.bool.isRequired,
  selectable: PropTypes.bool.isRequired,
  multi: PropTypes.bool.isRequired,
  selected: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  setSelected: PropTypes.func.isRequired,
  setExpandedId: PropTypes.func.isRequired,
  getPropertyTitle: PropTypes.func.isRequired,
  fetchBuildingProperties: PropTypes.func.isRequired,
  isPMA: PropTypes.bool,
  viewPMAUnits: PropTypes.func,
  viewLeaseUnitsHandler: PropTypes.func,
  isPMABank: PropTypes.bool,
  isPmc: PropTypes.bool,
};

PropertySummary.defaultProps = {
  expandedId: null,
  selected: null,
  isPMA: false,
  viewPMAUnits: () => {},
  viewLeaseUnitsHandler: () => {},
  isPMABank: false,
  isPmc: false,
};

export default withStyles(styles)(injectIntl(memo(PropertySummary)));
